import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (t: Theme) => void;
  initTheme: () => void;
}

const STORAGE_KEY = 'app-theme';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(t: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (t === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  root.style.colorScheme = t;
  // Notify any listeners (e.g. iframes, charts) that theme changed
  try {
    window.dispatchEvent(new CustomEvent('themechange', { detail: t }));
  } catch {
    // no-op
  }
}

let systemListenerAttached = false;

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      resolved: 'light',
      setTheme: (theme) => {
        const resolved = theme === 'system' ? getSystemTheme() : theme;
        applyTheme(resolved);
        set({ theme, resolved });
      },
      initTheme: () => {
        const { theme } = get();
        const resolved = theme === 'system' ? getSystemTheme() : theme;
        applyTheme(resolved);
        set({ resolved });

        if (!systemListenerAttached && typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
          const mq = window.matchMedia('(prefers-color-scheme: dark)');
          const handler = () => {
            const current = get().theme;
            if (current === 'system') {
              const next = getSystemTheme();
              applyTheme(next);
              set({ resolved: next });
            }
          };
          // Modern browsers
          if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', handler);
          } else if (typeof (mq as any).addListener === 'function') {
            (mq as any).addListener(handler);
          }
          systemListenerAttached = true;
        }
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ theme: state.theme }),
    }
  )
);
